import type { ComponentType } from "react";
import React from "react";
import { DropTarget } from "react-dnd";
import { NativeTypes } from "react-dnd-html5-backend";
import classNames from "classnames";
import FileSelector from "components/files/composer/FileSelector";
import { FileData } from "components/../@types/files";

type FileInputArgs = {
  connectDropTarget?: (element: React.ReactElement<any, any>) => void;
  onSelectFile: (e: any) => void;
  multiple?: boolean;
  acceptedType: string;
  empty: boolean;
  isImage: boolean;
  isOver?: boolean;
  selectExisting?: boolean;
  onSelectExistingFile: (selectedFiles: FileData[]) => void;
  onDropFile: ({ files }: { files: any }) => void;
  storageDirectory: string;
  disabled?: boolean;
};

function getAcceptedFileTypes(
  storageDirectory: string,
  acceptedType: string,
): string {
  if (
    storageDirectory !== "files" ||
    !Preload.current_network.files.extension_filter_available ||
    !Preload.current_network.files.extension_filter_enabled
  ) {
    return acceptedType;
  }

  const filterType = Preload.current_network.files.extension_filter_type;
  const filterList = Preload.current_network.files.extension_filter_list;

  // Currently, only `*` is passed as `acceptedType` for `storageDirectory === ‘files’`,
  // so we can simply replace this here. Should this change in the future,
  // we will have to consider how to reconcile the `filterList` and the `acceptedType`.
  if (filterType === "allow" && Array.isArray(filterList)) {
    return "." + filterList.join(",.");
  }

  return acceptedType;
}

function validateFile(file: File, storageDirectory: string): boolean {
  if (
    storageDirectory !== "files" ||
    !Preload.current_network.files.extension_filter_available ||
    !Preload.current_network.files.extension_filter_enabled
  ) {
    return true;
  }

  const filterType = Preload.current_network.files.extension_filter_type;
  const filterList = Preload.current_network.files.extension_filter_list;

  const fileExtension = file.name.match(/\.(\w+)$/)?.[1];

  if (filterType === "allow") {
    return filterList.includes(fileExtension as string);
  }

  return !filterList.includes(fileExtension as string);
}

function createFileSelectHandler(
  originalHandler: (e: any) => void,
  storageDirectory: string,
) {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const allFiles = Array.from(e.target.files);
      const invalidFiles = allFiles.filter(
        (file) => !validateFile(file, storageDirectory),
      );

      if (invalidFiles.length > 0) {
        const filterType = Preload.current_network.files.extension_filter_type;
        if (filterType === "allow") {
          const filterList =
            Preload.current_network.files.extension_filter_list;
          toastr.error(
            I18n.t("errors.uploader.file_type_not_accepted_allow_list", {
              filename: invalidFiles.map((file) => file.name).join(", "),
              allowedExtensions: "." + filterList.join(", ."),
            }),
          );
        } else {
          toastr.error(
            I18n.t("errors.uploader.file_type_not_accepted", {
              filename: invalidFiles.map((file) => file.name).join(", "),
            }),
          );
        }
      }

      const validFiles = allFiles.filter(
        (file) => !invalidFiles.includes(file),
      );

      if (validFiles.length === 0) {
        return;
      }

      const dataTransfer = new DataTransfer();
      validFiles.forEach((file) => dataTransfer.items.add(file));

      e.target.files = dataTransfer.files;
    }

    originalHandler(e);
  };
}

function FileInput({
  connectDropTarget,
  onSelectFile,
  multiple,
  acceptedType,
  empty,
  isImage,
  isOver,
  selectExisting,
  onSelectExistingFile,
  disabled = false,
  storageDirectory,
}: FileInputArgs) {
  const effectiveAcceptedType = getAcceptedFileTypes(
    storageDirectory,
    acceptedType,
  );

  const handleSelectFile = createFileSelectHandler(
    onSelectFile,
    storageDirectory,
  );

  return multiple || (!multiple && empty) ? (
    connectDropTarget?.(
      <div className="filesInput flex-1">
        <div
          className={classNames(
            "flex flex-col gap-2 h-full items-center justify-center min-h-[100px] uploader-dropzone",
            { isOver },
          )}
        >
          <p
            className={classNames("font-medium", { "text-muted": !!disabled })}
          >
            {I18n.t(
              `js.files.uploader.drag_and_drop_${
                isImage ? (multiple ? "images" : "image") : "files"
              }`,
            )}
          </p>
          <label
            className={classNames(
              "fileInputButton btn btn-light btn-sm fileFocusButton",
              { disabled: !!disabled },
            )}
          >
            <i className="fa-regular fa-upload" />{" "}
            {I18n.t(
              `js.files.uploader.select_${isImage ? "image" : "file"}${
                multiple ? "s" : ""
              }`,
            )}
            <input
              type="file"
              onChange={handleSelectFile}
              multiple={multiple}
              accept={effectiveAcceptedType}
              style={{ width: "1px", opacity: 0 }}
              disabled={disabled || (!multiple && !empty)}
            />
          </label>
          {selectExisting ? (
            <FileSelector
              currentRootFolder={
                Tixxt.currentContext
                  ? Tixxt.currentContext.rootFolder().toJSON()
                  : Tixxt.currentNetwork.rootFolder().toJSON()
              }
              onSelectFiles={onSelectExistingFile}
              disabled={disabled}
            />
          ) : null}
        </div>
      </div>,
    )
  ) : (
    <div className="filesInput">
      <div className="filesDropTarget">
        <p>
          {I18n.t(
            `js.files.uploader.remove_${isImage ? "image" : "file"}_hint`,
          )}
        </p>
      </div>
    </div>
  );
}

export default DropTarget(
  [NativeTypes.FILE],
  {
    drop(props: { onDropFile(arg: unknown) }, monitor) {
      props.onDropFile(monitor.getItem());
      return props;
    },
  },
  (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver(),
  }),
)(FileInput as ComponentType<FileInputArgs>);
